import { SignupBodySchema } from '@copilot/shared';
import { AudioLines } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { Button, ErrorBanner, Field, Input } from '../components';
import { FullPageSpinner } from './guards';
import { useAuth } from './AuthProvider';
import { homePathFor } from './roles';

function AuthShell({ title, children, footer }: { title: string; children: ReactNode; footer: ReactNode }) {
  return (
    <div className="bg-sidebar-gradient flex min-h-screen items-center justify-center p-4">
      <div className="rise w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <span className="bg-brand-gradient grid h-9 w-9 place-items-center rounded-xl">
            <AudioLines aria-hidden className="h-5 w-5 text-white" />
          </span>
          <span className="text-base font-semibold text-sidebar-ink">Clustox Sales Copilot</span>
        </div>
        <div className="rounded-card border border-line bg-surface p-6 shadow-card">
          <h1 className="mb-4 text-lg font-semibold tracking-tight text-ink">{title}</h1>
          {children}
        </div>
        <p className="mt-4 text-center text-sm text-sidebar-muted [&_a]:text-sidebar-ink [&_a]:underline">{footer}</p>
      </div>
    </div>
  );
}

function useRedirectTarget(): string | null {
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  return from && from.startsWith('/') && !from.startsWith('/login') ? from : null;
}

export function LoginPage() {
  const { status, user, login } = useAuth();
  const from = useRedirectTarget();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'loading') return <FullPageSpinner />;
  if (user) return <Navigate to={from ?? homePathFor(user.role)} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Once the session is set, the <Navigate> above redirects.
      await login(email.trim(), password);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Sign in"
      footer={
        <>
          No account yet?{' '}
          <Link to="/signup">
            Create one
          </Link>
        </>
      }
    >
      <form className="space-y-3" onSubmit={(e) => void submit(e)}>
        <Field label="Email">
          <Input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        </Field>
        <Field label="Password">
          <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <ErrorBanner error={error} />
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          Sign in
        </Button>
      </form>
    </AuthShell>
  );
}

export function SignupPage() {
  const { status, user, signup } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'loading') return <FullPageSpinner />;
  if (user) return <Navigate to={homePathFor(user.role)} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = SignupBodySchema.safeParse({ ...form, email: form.email.trim(), name: form.name.trim() });
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? '');
        errs[key] ??= key === 'password' ? 'Use at least 10 characters.' : issue.message;
      }
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setBusy(true);
    setError(null);
    try {
      await signup(parsed.data.email, parsed.data.password, parsed.data.name);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <AuthShell
      title="Create account"
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login">
            Sign in
          </Link>
        </>
      }
    >
      <form className="space-y-3" onSubmit={(e) => void submit(e)} noValidate>
        <Field label="Name" error={fieldErrors.name}>
          <Input autoComplete="name" value={form.name} onChange={set('name')} autoFocus />
        </Field>
        <Field label="Work email" error={fieldErrors.email} hint="If you were invited, use the invited address.">
          <Input type="email" autoComplete="email" value={form.email} onChange={set('email')} />
        </Field>
        <Field label="Password" error={fieldErrors.password} hint="At least 10 characters.">
          <Input type="password" autoComplete="new-password" value={form.password} onChange={set('password')} />
        </Field>
        <ErrorBanner error={error} />
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          Create account
        </Button>
      </form>
    </AuthShell>
  );
}
